import { useSession } from 'next-auth/react';
import GridDragAndDrop from '../../../../../Misc/GridDragAndDrop/GridDragAndDrop';
import styles from './styles.module.scss';
import { useMemo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableList, faSave, faToolbox, faEdit } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from 'react-number-format';
import { isEqual } from 'lodash';

import errorMsg from '../../../../../Functions/errorMsg';
import successMsg from '../../../../../Functions/successMsg';

import axios from 'axios';
import { BounceLoader } from 'react-spinners';

import Resizer from 'react-image-file-resizer';
import Checkbox from '../../../../../FunctionalComponents/CustomCheckbox/Checkbox';
import DropMenu from '../../../../../FunctionalComponents/CustomDropMenu/DropMenu';
import ToggleSwitch from '../../../../../FunctionalComponents/ToggleSwitch';
import TextInput from '../../../../../FunctionalComponents/CustomTextInput/TextInput';
import Textarea from '../../../../../FunctionalComponents/CustomTextarea/Textarea';

const resizeFile = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      1500,
      1500,
      'PNG',
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64'
    );
  });

export default function EditService({ serviceID, close }) {
  const { data: session, status: sessionStatus } = useSession();

  const [hasError, setHasError] = useState(false);
  const [loadingService, setLoadingService] = useState(false);
  const [serviceData, setServiceData] = useState(null);

  const [pendingRequest, setPendingRequest] = useState(false);
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [serviceCost, setServiceCost] = useState('');
  const [costMode, setCostMode] = useState('');
  const [serviceCategory, setServiceCategory] = useState('');
  const [serviceStatus, setServiceStatus] = useState('Draft');
  const [uploads, setUploads] = useState([]);

  const [isPublishEnabled, setIsPublishEnabled] = useState(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  const [availability, setAvailability] = useState({
    Monday: { enabled: false, start: '', end: '' },
    Tuesday: { enabled: false, start: '', end: '' },
    Wednesday: { enabled: false, start: '', end: '' },
    Thursday: { enabled: false, start: '', end: '' },
    Friday: { enabled: false, start: '', end: '' },
    Saturday: { enabled: false, start: '', end: '' },
    Sunday: { enabled: false, start: '', end: '' },
  });

  function handleCheckboxChange(day) {
    setAvailability((prevState) => ({
      ...prevState,
      [day]: {
        ...prevState[day],
        enabled: !prevState[day].enabled,
      },
    }));
  }

  function handleTimeChange(day, type, value) {
    setAvailability((prevState) => ({
      ...prevState,
      [day]: {
        ...prevState[day],
        [type]: value,
      },
    }));
  }

  function handleServiceNameChange(text) {
    let value = text;
    value = value.replace(/[^a-zA-Z0-9-:& ]/g, '');
    value = value.replace(/\s{2,}/g, ' ');
    setServiceName(value);
  }

  function handleServiceDescriptionChange(text) {
    const formattedDescription = text.replace(/[^a-zA-Z0-9!@#$%&()/.,"':;[\]~+=-_ ]/g, '');
    setServiceDescription(formattedDescription);
  }

  function handleCategoryChange(text) {
    let value = text.replace(/[^a-zA-Z0-9 ]/g, '');
    value = value.replace(/\b\w/g, (char) => char.toUpperCase());
    setServiceCategory(value);
  }

  useEffect(() => {
    if (loadingService || serviceData || sessionStatus !== 'authenticated' || hasError) return;

    async function fetchServiceData() {
      setLoadingService(true);
      try {
        const getResult = await axios.post(`${window.location.origin}/api/vendor/manage/services/getServiceData`, {
          serviceID: serviceID,
        });

        if (getResult && getResult.status === 200) {
          const { name, description, cost, category, status, images, costMode, availability } = getResult.data;

          const sortedImages = images.sort((a, b) => a.position - b.position);

          setServiceData(getResult.data);

          setServiceName(name);
          setServiceDescription(description);
          setServiceCost(cost);
          setCostMode(costMode);
          setServiceCategory(category);
          setServiceStatus(status);
          setAvailability(availability);
          setUploads(sortedImages || []);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true);
      } finally {
        setLoadingService(false);
      }
    }

    if (session && sessionStatus === 'authenticated') {
      fetchServiceData();
    }
  }, [loadingService, serviceData, session, sessionStatus, serviceID, hasError]);

  useEffect(() => {
    if (!serviceData || loadingService) return;
  
    const validateAvailability = () => {
      for (const day in availability) {
        if (availability[day].enabled) {
          const { start, end } = availability[day];
          if (!start || !end) return false;
  
          const startTime = new Date(`1970-01-01T${start}`);
          const endTime = new Date(`1970-01-01T${end}`);
  
          if (startTime >= endTime) return false; // Start time must be before end time
        }
      }
      return Object.values(availability).some((day) => day.enabled); // At least one day must be enabled
    };
  
    // Validation for Publish
    const isFormValid =
      serviceName.length >= 5 &&
      serviceName.length <= 42 &&
      serviceDescription.length >= 24 &&
      serviceDescription.length <= 1000 &&
      serviceCost > 0 &&
      costMode &&
      serviceCategory.length >= 4 &&
      serviceCategory.length <= 50 &&
      uploads.length > 0 &&
      validateAvailability();
  
    // Check if data has changed for Save

    const isDataEdited =
      serviceName !== serviceData.name ||
      serviceDescription !== serviceData.description ||
      parseFloat(serviceCost) !== parseFloat(serviceData.cost) ||
      serviceCategory !== serviceData.category ||
      serviceStatus !== serviceData.status ||
      !isEqual(costMode, serviceData.costMode) ||
      !isEqual(availability, serviceData.availability) ||
      !isEqual(uploads, serviceData.images);
  
    // Publish Enabled: Only if form is valid and data has changed
    setIsPublishEnabled(isFormValid && isDataEdited);
  
    // Save Enabled: Only if data has changed
    setIsSaveEnabled(isDataEdited);
  }, [
    serviceName,
    serviceDescription,
    serviceCost,
    costMode,
    serviceCategory,
    uploads,
    availability,
    serviceStatus,
    serviceData,
    loadingService,
  ]);  
  

  async function prepareImagesForUpload(images) {
    return Promise.all(
      images.map(async (image, index) => {
        if (image.url.startsWith('blob:')) {
          const resizedImage = await resizeFile(image.imageFile);
          return { position: index + 1, url: resizedImage };
        }
        return { position: index + 1, url: image.url };
      })
    );
  }

  // Handler for Toggle Switch
  function handleStatusChange(newState) {
    setServiceStatus(newState ? 'Active' : 'Inactive');
  }

  async function publishService() {
    if (pendingRequest) return;

    setPendingRequest(true);

    if (!isPublishEnabled) {
      errorMsg('Please ensure all required fields are correctly filled and changes have been made.');
      setPendingRequest(false);
      return;
    }

  await new Promise((resolve) => setTimeout(resolve, 0));

  const updatedStatus = serviceStatus === 'Draft' ? 'Active' : serviceStatus;

  const preparedImages = await prepareImagesForUpload(uploads);

  const servicePayload = {
    serviceName,
    serviceDescription,
    serviceCost,
    serviceCategory,
    costMode,
    images: preparedImages,
    availability,
    status: updatedStatus,
  };

    try {
      const result = await axios.post(`${window.location.origin}/api/vendor/manage/services/saveService`, {
        serviceID: serviceID,
        serviceData: servicePayload
      });

      if (result && result.status === 200) {
        setServiceStatus(updatedStatus);
        successMsg('Service changes are now saved.');
        close();
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setPendingRequest(false);
    }
  }


  async function saveDraftService() {
    if (pendingRequest) return;

    setPendingRequest(true);

    if (!isSaveEnabled) {
      errorMsg('Please make changes or upload images to save a draft.');
      setPendingRequest(false);
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 0));

    const preparedImages = await prepareImagesForUpload(uploads);

    const servicePayload = {
      serviceName,
      serviceDescription,
      serviceCost,
      serviceCategory,
      costMode,
      images: preparedImages,
      availability,
      status: 'Draft',
    };

    try {
      const result = await axios.post(`${window.location.origin}/api/vendor/manage/services/saveService`, {
        serviceID: serviceID,
        serviceData: servicePayload
      });

      if (result && result.status === 200) {
        successMsg('Service saved under drafts.');
        close();
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setPendingRequest(false);
    }
  }

  const memoizeImageGrid = useMemo(
    () => (
      <GridDragAndDrop
        initialImages={serviceData?.images || []}
        pendingRequest={pendingRequest}
        receiveUploadData={(newUploads) => setUploads(newUploads)}
      />
    ),
    [pendingRequest, serviceData?.images]
  );

  // Loading State
  function isLoading() {
    return loadingService || sessionStatus === 'loading' || !serviceData || pendingRequest;
  }

  if (isLoading()) {
    return (
      <section id={styles.loading}>
        <BounceLoader size={75} speedMultiplier={2} color="#154734" />
      </section>
    );
  }

  return (
    <section id={styles.editServiceWrapper}>
      <div className={styles.detailsContainer}>
        <h1>Edit Service <FontAwesomeIcon icon={faToolbox} /></h1>
        <section className={styles.mainInfoContainer}>
          <div>
            <h1>Name</h1>
            <TextInput minCharacters={5} maxCharacters={42} value={serviceName} onChange={handleServiceNameChange} />
          </div>
          <div>
            <h1>Cost</h1>
            <section>
              <div id={styles.dropMenuContainer}><DropMenu options={[ '/ hr', 'Flat Rate' ]} onSelect={(optionValue) => setCostMode(optionValue)} placeholder='Select Unit' defaultValue={costMode} /></div>
              <NumberFormat onValueChange={(values) => setServiceCost(parseFloat(values.value))} isAllowed={costValueCap} placeholder="$14.99" decimalScale={2} thousandSeparator={true} prefix={'$'} value={serviceCost} />
            </section>
          </div>
        </section>
        <div className={styles.basicInfoCard}>
          <div>
            <h1>Description</h1>
            <section>
              <Textarea minHeight='100%' maxHeight='100%' resize='none' minCharacters={24} maxCharacters={1000} value={serviceDescription} onChange={handleServiceDescriptionChange} />
            </section>
            
          </div>
          <div>
            <h1>
              Category <FontAwesomeIcon icon={faTableList} id={styles.icon} />
            </h1>
            <section>
              <TextInput minCharacters={4} maxCharacters={50} placeholder="Service Category" onChange={handleCategoryChange} value={serviceCategory} />
            </section>
          </div>
        </div>
        <section className={styles.basicInfoContainer}>
          <div id={styles.timesAvailable}>
            <div>
              <h1>Service Dates</h1>
            </div>
            {Object.keys(availability).map((day) => {
              const { enabled, start, end } = availability[day];
              return (
                <div className={styles.dayItem} key={day}>
                  <div>
                    <Checkbox
                      checked={enabled}
                      onChange={() => handleCheckboxChange(day)}
                    />
                    <h1>{day}</h1>
                  </div>
                  {enabled && (
                    <div>
                      <section>
                        <h1>Start</h1>
                        <input
                          type="time"
                          value={start}
                          onChange={(e) => handleTimeChange(day, 'start', e.target.value)}
                        />
                      </section>
                      <section>
                        <h1>End</h1>
                        <input
                          type="time"
                          value={end}
                          onChange={(e) => handleTimeChange(day, 'end', e.target.value)}
                        />
                      </section>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
        <div className={styles.actionBar}>
          <section id={styles.buttonGroup}>
            <button disabled={!isPublishEnabled} onClick={publishService} className={styles.publishButton}>
              {serviceStatus === 'Draft' ? 'Publish' : 'Save'} <FontAwesomeIcon icon={faEdit} />
            </button>
            {serviceStatus === 'Draft' && (
              <button
                disabled={!isSaveEnabled}
                onClick={saveDraftService}
                className={styles.draftButton}
              >
                Save <FontAwesomeIcon icon={faSave} />
              </button>
            )}
            <button onClick={close} className={styles.cancelButton}>
              Cancel
            </button>
          </section>

          {serviceStatus !== 'Draft' && (
            <section id={styles.activeService}>
              <h1>Service Active: </h1>
              <ToggleSwitch
                isDisabled={pendingRequest}
                state={serviceStatus === 'Active'}
                setState={handleStatusChange}
              />
            </section>
          )}
        </div>
      </div>
      <div className={styles.uploadContainer}>
        <section>{memoizeImageGrid}</section>
        <section>
          <h1>Service Images</h1>
          <h2>{uploads.length} / 12 Slots</h2>
        </section>
      </div>
    </section>
  );
}

/* Misc Functions */

const costValueCap = (inputObj) => {
  const { value } = inputObj;
  return value <= 1000000000;
};